// Client abbreviation helpers: configurable per client (via client.icon or
// preferences), with intelligent autogeneration as default.

const CORP_SUFFIXES = /\b(inc|incorporated|llc|ltd|limited|corp|corporation|co|company|group|holdings|technologies|tech|studios|studio)\b/gi;

/**
 * Autogenerate a clean 2-4 character abbreviation from a client name.
 */
export function autogenerateClientAbbreviation(name: string): string {
  const trimmed = (name ?? "").toString().trim();
  if (!trimmed) return "";

  // If already short (e.g. "IBM", "HP", "SB"), use it uppercase
  if (trimmed.length <= 3) return trimmed.toUpperCase();

  // Strip common corporate suffixes
  const stripped = trimmed.replace(CORP_SUFFIXES, "").trim() || trimmed;
  const words = stripped.split(/[\s\-_/]+/).filter(Boolean);

  if (words.length >= 2) {
    // Multi-word: take first character of each word (up to 4)
    return words.map((w) => w[0]).join("").toUpperCase().slice(0, 4);
  }

  const single = words[0] || trimmed;

  // If camelCase or PascalCase with multiple capitals (e.g. "AfterParty", "SuperCell")
  const midCaps = single.match(/[A-Z]/g);
  if (midCaps && midCaps.length >= 2 && midCaps.length <= 4) {
    return midCaps.join("").toUpperCase();
  }

  // Short single word (<= 4 chars): full word uppercase (e.g. "ACME", "SONY")
  if (single.length <= 4) {
    return single.toUpperCase();
  }

  // Common well-known corporate abbreviations
  const known: Record<string, string> = {
    walmart: "WMT",
    microsoft: "MSFT",
    google: "GOOG",
    amazon: "AMZN",
    apple: "AAPL",
    facebook: "META",
    netflix: "NFLX",
  };
  const lower = single.toLowerCase();
  if (known[lower]) return known[lower];

  // Default: first 3 characters uppercase (e.g. "HAR", "PAR")
  return single.slice(0, 3).toUpperCase();
}

/**
 * Resolves the abbreviation for a client.
 * Priority order:
 * 1. User/workspace preferences override (if provided)
 * 2. Explicit abbreviation on client record (client.abbreviation or client.icon)
 * 3. Autogenerated abbreviation from client name
 */
export function getClientAbbreviation(
  client: any,
  overrides?: Record<string, string> | null
): string {
  if (!client) return "";

  const id = typeof client === "object" ? client?.id : null;
  const name = typeof client === "object" ? client?.name : String(client);

  // 1. Preference override
  if (overrides) {
    if (id && overrides[id]) return overrides[id].trim().toUpperCase();
    if (name && overrides[name]) return overrides[name].trim().toUpperCase();
  }

  // 2. Explicit client record field (abbreviation or icon)
  if (typeof client === "object") {
    const explicit = (client?.abbreviation ?? client?.icon ?? "").toString().trim();
    if (explicit) return explicit.toUpperCase();
  }

  // 3. Autogenerate from name
  return autogenerateClientAbbreviation(name);
}

/**
 * Autogenerate a clean 2-4 character abbreviation from a project name.
 */
export function autogenerateProjectAbbreviation(name: string): string {
  return autogenerateClientAbbreviation(name);
}

/**
 * Resolves the abbreviation for a project.
 * Priority order:
 * 1. User/workspace preferences override (if provided)
 * 2. Explicit project record field (project.abbreviation, project.job_code, or project.notes_json?.abbreviation)
 * 3. Autogenerated abbreviation from project name
 */
export function getProjectAbbreviation(
  project: any,
  overrides?: Record<string, string> | null
): string {
  if (!project) return "";

  const id = typeof project === "object" ? project?.id : null;
  const name = typeof project === "object" ? project?.name : String(project);

  // 1. Preference override
  if (overrides) {
    if (id && overrides[id]) return overrides[id].trim().toUpperCase();
    if (name && overrides[name]) return overrides[name].trim().toUpperCase();
  }

  // 2. Explicit project record field
  if (typeof project === "object") {
    const explicit = (
      project?.abbreviation ??
      project?.notes_json?.abbreviation ??
      project?.job_code ??
      ""
    ).toString().trim();
    if (explicit && explicit.length <= 6) return explicit.toUpperCase();
  }

  // 3. Autogenerate from name
  return autogenerateProjectAbbreviation(name);
}

/**
 * Checks if a client or project matches a search query by its abbreviation
 * (explicit icon, abbreviation, job_code, preference override, or autogenerated abbreviation).
 */
export function matchesAbbreviation(
  record: any,
  query: string,
  overrides?: Record<string, string> | null
): boolean {
  if (!record || !query) return false;
  const q = query.trim().toUpperCase();
  if (!q) return false;

  // 1. Explicit acronym/icon/abbreviation fields
  const explicit = (
    record.abbreviation ??
    record.icon ??
    record.job_code ??
    record.notes_json?.abbreviation ??
    ""
  ).toString().trim().toUpperCase();
  if (explicit && (explicit === q || explicit.includes(q) || q.includes(explicit))) return true;

  // 2. User preference override
  const id = typeof record === "object" ? record?.id : null;
  if (overrides && id && overrides[id]) {
    const ov = overrides[id].trim().toUpperCase();
    if (ov === q || ov.includes(q) || q.includes(ov)) return true;
  }

  // 3. Autogenerated abbreviation from name
  const name = typeof record === "object" ? record?.name : String(record);
  if (name) {
    const auto = autogenerateClientAbbreviation(name).toUpperCase();
    if (auto && (auto === q || auto.includes(q) || (q.length >= 2 && auto.startsWith(q)))) return true;
  }

  return false;
}

/**
 * Extracts a project ID from a URL if the URL targets a project.
 */
export function projectIdFromUrl(url: string): string | null {
  if (!url || typeof url !== "string") return null;
  const pathMatch = url.match(/^\/(?:portal\/)?projects\/([^/?#]+)/);
  if (pathMatch) return pathMatch[1];
  try {
    const qIndex = url.indexOf("?");
    if (qIndex >= 0) {
      const sp = new URLSearchParams(url.slice(qIndex));
      const qProj = sp.get("project");
      if (qProj) return qProj;
    }
  } catch {
    /* ignore */
  }
  return null;
}

export type FavoriteDisplayInfo = {
  displayLabel: string;
  clientName?: string;
  clientAbbr?: string;
};

/**
 * Resolves the display label and company abbreviation for a sidebar favorite.
 * Handles:
 * - Stored client and clientAbbr on the favorite
 * - Resolving the project from the URL against projectsById
 * - Inferred client from "Project Name aka Client Name"
 * - Cleans duplicate "(Client)" strings from the label
 */
export function resolveFavoriteDisplay(
  favorite: { url: string; label: string; client?: string; clientAbbr?: string },
  projectsById?: Map<string, any> | null,
  overrides?: Record<string, string> | null
): FavoriteDisplayInfo {
  let label = favorite.label || "";
  let clientObj: any = favorite.client;
  const explicitAbbr: string | undefined = favorite.clientAbbr;

  // 1. If label contains " aka ", e.g. "Event Portal aka Venue Core"
  if (/\s+aka\s+/i.test(label)) {
    const parts = label.split(/\s+aka\s+/i);
    if (parts.length === 2 && parts[0].trim() && parts[1].trim()) {
      label = parts[0].trim();
      if (!clientObj) {
        clientObj = parts[1].trim();
      }
    }
  }

  // 2. If url is a project URL and we have a project map, look up project
  if (projectsById) {
    const pid = projectIdFromUrl(favorite.url);
    if (pid && projectsById.has(pid)) {
      const proj = projectsById.get(pid);
      if (proj?.client) {
        clientObj = proj.client;
      }
    }
  }

  // 3. Resolve client abbreviation
  const clientName = typeof clientObj === "object" ? clientObj?.name : clientObj ? String(clientObj) : undefined;
  const clientAbbr = explicitAbbr || (clientObj ? getClientAbbreviation(clientObj, overrides) : undefined);

  // 4. Clean label if it already has (ClientName) or (ClientAbbr) attached
  if (clientAbbr) {
    label = label.replace(new RegExp(`\\s*\\(${clientAbbr}\\)$`, "i"), "").trim();
  }
  if (clientName) {
    label = label.replace(new RegExp(`\\s*\\(${clientName}\\)$`, "i"), "").trim();
  }

  return {
    displayLabel: label,
    clientName,
    clientAbbr,
  };
}
